import React, { useState, useEffect } from 'react';
import { Smartphone, Menu, Bell, Ticket, Search, BookOpen, MessageCircle, Plus, Filter, Clock, User, Download, Star, Eye, ThumbsUp, Wifi, WifiOff, CheckCircle, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import MockDataService from '../../../services/MockDataService';

const AgentMobileAccess = () => {
  const [mobileData, setMobileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const fetchMobileData = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getAgentMobileAccess('agent-001');
        if (response.success) {
          setMobileData(response.data);
        } else {
          setError('Failed to load mobile data');
        }
      } catch (err) {
        setError('An error occurred while fetching mobile data.');
        console.error('Error fetching mobile data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMobileData();
  }, []);

  if (loading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading mobile data...</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-500 dark:text-red-400">Error: {error}</div>;
  }

  if (!mobileData) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">No mobile data available.</div>;
  }

  const { agentMobileAccess, mobileStats } = mobileData;
  const { mobileDashboard, mobileTicketManagement, mobileNotifications, mobileChat, mobileKnowledgeBase, mobileProfile, offlineSupport, mobileSettings } = agentMobileAccess;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Smartphone },
    { id: 'tickets', label: 'Tickets', icon: Ticket },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'chat', label: 'Chat', icon: MessageCircle },
    { id: 'knowledge', label: 'Knowledge', icon: BookOpen },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'settings', label: 'Settings', icon: Smartphone },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'in-progress': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'resolved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'info': return <CheckCircle className="h-4 w-4 text-blue-500" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Tickets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileDashboard.quickStats.openTickets}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Assigned to Me</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileDashboard.quickStats.assignedToMe}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved Today</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileDashboard.quickStats.resolvedToday}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">SLA Status</p>
                <div className="flex items-center justify-center space-x-1">
                  <div className={`w-2 h-2 rounded-full ${mobileDashboard.quickStats.slaPercentage >= 90 ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{mobileDashboard.quickStats.slaPercentage}%</p>
                </div>
              </div>
            </div>

            {/* My Tickets */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">My Tickets</h3>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {mobileDashboard.myTickets.slice(0, 3).map((ticket) => (
                  <div key={ticket.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{ticket.title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{ticket.customer}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                      <span>{new Date(ticket.lastUpdated).toLocaleDateString()}</span>
                      <div className="flex items-center space-x-3">
                        {ticket.attachments > 0 && (
                          <span className="flex items-center space-x-1">
                            <Download className="h-3 w-3" />
                            <span>{ticket.attachments}</span>
                          </span>
                        )}
                        {ticket.comments > 0 && (
                          <span className="flex items-center space-x-1">
                            <MessageCircle className="h-3 w-3" />
                            <span>{ticket.comments}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {mobileDashboard.quickActions.map((action) => (
                  <button
                    key={action.id}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="text-2xl mb-2">{action.icon}</div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{action.name}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{action.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Activity</h3>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {mobileDashboard.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <div className="text-lg">{activity.icon}</div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{activity.description}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerts */}
            {mobileDashboard.alerts.length > 0 && (
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Alerts</h3>
                <div className="space-y-3">
                  {mobileDashboard.alerts.map((alert) => (
                    <div key={alert.id} className={`p-3 rounded-md border ${
                      alert.severity === 'critical' ? 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700' :
                      alert.severity === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700' :
                      'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700'
                    }`}>
                      <div className="flex items-start space-x-3">
                        {getSeverityIcon(alert.severity)}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{alert.message}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(alert.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'tickets':
        return (
          <div className="space-y-6">
            {/* Ticket Management Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket Management</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage your tickets on mobile</p>
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <Search className="h-4 w-4" />
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <Filter className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Ticket Filters */}
            <div className="flex space-x-2 overflow-x-auto">
              {mobileTicketManagement.ticketFilters.map((filter) => (
                <button
                  key={filter.id}
                  className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full whitespace-nowrap hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {filter.name}
                </button>
              ))}
            </div>

            {/* Ticket List */}
            <div className="space-y-3">
              {mobileDashboard.myTickets.map((ticket) => (
                <div key={ticket.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{ticket.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{ticket.customer} • {ticket.department}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{ticket.description}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-3">
                      <span>{new Date(ticket.createdDate).toLocaleDateString()}</span>
                      <span>{new Date(ticket.lastUpdated).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      {ticket.attachments > 0 && (
                        <span className="flex items-center space-x-1">
                          <Download className="h-3 w-3" />
                          <span>{ticket.attachments}</span>
                        </span>
                      )}
                      {ticket.comments > 0 && (
                        <span className="flex items-center space-x-1">
                          <MessageCircle className="h-3 w-3" />
                          <span>{ticket.comments}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mt-3">
                    {ticket.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      {mobileTicketManagement.ticketActions.slice(0, 3).map((action) => (
                        <button
                          key={action.id}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                        >
                          <span className="text-lg">{action.icon}</span>
                        </button>
                      ))}
                    </div>
                    <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800">
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            {/* Notifications Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Mobile Notifications</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Stay updated on the go</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 text-xs font-semibold bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full">
                  {mobileNotifications.badgeCount} unread
                </span>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                  Mark All Read
                </button>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Notification Settings</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Push Notifications</span>
                  <div className={`w-10 h-6 rounded-full ${mobileNotifications.notificationSettings.pushEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileNotifications.notificationSettings.pushEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Sound</span>
                  <div className={`w-10 h-6 rounded-full ${mobileNotifications.notificationSettings.soundEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileNotifications.notificationSettings.soundEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Vibration</span>
                  <div className={`w-10 h-6 rounded-full ${mobileNotifications.notificationSettings.vibrationEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileNotifications.notificationSettings.vibrationEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Push Notifications */}
            <div className="space-y-3">
              {mobileNotifications.pushNotifications.map((notification) => (
                <div key={notification.id} className={`p-4 rounded-lg border ${
                  notification.read 
                    ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
                    : 'bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{notification.title}</h4>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{notification.body}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          notification.type === 'ticket' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          notification.type === 'chat' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          notification.type === 'system' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {notification.type}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          notification.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          notification.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                          notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {notification.priority}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(notification.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'chat':
        return (
          <div className="space-y-6">
            {/* Mobile Chat Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Team Chat</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Chat with your team on mobile</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {mobileChat.onlineAgents.length} online
                </span>
                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Chat Channels */}
            <div className="space-y-2">
              {mobileChat.channels.map((channel) => (
                <div
                  key={channel.id}
                  className={`p-3 rounded-md cursor-pointer transition-colors ${
                    channel.isActive
                      ? 'bg-blue-100 dark:bg-blue-900 border border-blue-300 dark:border-blue-700'
                      : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">#{channel.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{channel.type}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {channel.unreadCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full">
                          {channel.unreadCount}
                        </span>
                      )}
                      {channel.lastMessage && (
                        <div className="text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-32">
                            {channel.lastMessage.content}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(channel.lastMessage.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Messages */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">#general</h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {mobileChat.onlineAgents.length} online
                  </span>
                </div>
              </div>
              
              <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                {mobileChat.messages.map((message) => (
                  <div key={message.id} className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {message.senderName.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{message.senderName}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mt-1">{message.content}</p>
                      {message.reactions.length > 0 && (
                        <div className="flex items-center space-x-2 mt-2">
                          {message.reactions.map((reaction, index) => (
                            <button
                              key={index}
                              className="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                              <span>{reaction.emoji}</span>
                              <span className="text-gray-600 dark:text-gray-400">{reaction.count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <button className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                    <MessageCircle className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Online Agents */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Online Agents</h4>
              <div className="grid grid-cols-2 gap-3">
                {mobileChat.onlineAgents.map((agent) => (
                  <div key={agent.id} className="flex items-center space-x-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <div className={`w-3 h-3 rounded-full ${
                      agent.status === 'available' ? 'bg-green-500' : 'bg-yellow-500'
                    }`}></div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{agent.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {agent.status === 'available' ? 'Available' : 'Busy'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'knowledge':
        return (
          <div className="space-y-6">
            {/* Knowledge Base Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Knowledge Base</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Browse articles on mobile</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {mobileKnowledgeBase.categories.map((category) => (
                <div key={category.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-lg flex items-center justify-center ${
                    category.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900' :
                    category.color === 'green' ? 'bg-green-100 dark:bg-green-900' :
                    category.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900' :
                    category.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900' :
                    'bg-red-100 dark:bg-red-900'
                  }`}>
                    <BookOpen className={`h-6 w-6 ${
                      category.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                      category.color === 'green' ? 'text-green-600 dark:text-green-400' :
                      category.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                      category.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                      'text-red-600 dark:text-red-400'
                    }`} />
                  </div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{category.name}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{category.articleCount} articles</p>
                </div>
              ))}
            </div>

            {/* Articles */}
            <div className="space-y-3">
              {mobileKnowledgeBase.articles.map((article) => (
                <div key={article.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{article.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{article.summary}</p>
                    </div>
                    {article.isBookmarked && (
                      <Star className="h-4 w-4 text-yellow-500" />
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                      {article.category}
                    </span>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Eye className="h-3 w-3" />
                        <span>{article.views}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="h-3 w-3 text-yellow-500" />
                        <span>{article.rating}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-3">
                    {article.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Updated: {new Date(article.lastUpdated).toLocaleDateString()}
                    </span>
                    <button className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800">
                      Read Article
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Bookmarks */}
            {mobileKnowledgeBase.bookmarks.length > 0 && (
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Bookmarked Articles</h4>
                <div className="space-y-2">
                  {mobileKnowledgeBase.bookmarks.map((bookmark) => (
                    <div key={bookmark.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{bookmark.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Bookmarked: {new Date(bookmark.bookmarkedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Star className="h-4 w-4 text-yellow-500" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-6">
            {/* Profile Header */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-medium text-blue-600 dark:text-blue-400">
                  {mobileProfile.personalInfo.firstName.charAt(0)}{mobileProfile.personalInfo.lastName.charAt(0)}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {mobileProfile.personalInfo.firstName} {mobileProfile.personalInfo.lastName}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{mobileProfile.workInfo.jobTitle}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{mobileProfile.workInfo.department}</p>
            </div>

            {/* Personal Information */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Personal Information</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Email</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{mobileProfile.personalInfo.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Phone</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{mobileProfile.personalInfo.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Employee ID</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{mobileProfile.workInfo.employeeId}</span>
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Preferences</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Language</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{mobileProfile.preferences.language}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Theme</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{mobileProfile.preferences.theme}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Notifications</span>
                  <div className={`w-10 h-6 rounded-full ${mobileProfile.preferences.notifications ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileProfile.preferences.notifications ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Stats */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Mobile Usage Stats</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.totalSessions}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Sessions</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.averageSessionTime}m</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Avg Session</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.mobileTicketsResolved}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Tickets Resolved</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.mobileChatMessages}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Chat Messages</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            {/* Settings Header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Mobile Settings</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Configure your mobile experience</p>
              </div>
              <div className="flex items-center space-x-2">
                {isOffline ? (
                  <div className="flex items-center space-x-1 text-orange-600 dark:text-orange-400">
                    <WifiOff className="h-4 w-4" />
                    <span className="text-sm">Offline</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                    <Wifi className="h-4 w-4" />
                    <span className="text-sm">Online</span>
                  </div>
                )}
              </div>
            </div>

            {/* General Settings */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">General Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Language</span>
                  <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Theme</span>
                  <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Font Size</span>
                  <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Auto Lock</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.general.autoLock ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.general.autoLock ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Notification Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Push Notifications</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.notifications.pushEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.notifications.pushEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Sound</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.notifications.soundEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.notifications.soundEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Vibration</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.notifications.vibrationEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.notifications.vibrationEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Badge Count</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.notifications.badgeEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.notifications.badgeEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Settings */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Data Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Auto Sync</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.data.autoSync ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.data.autoSync ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Sync Frequency</span>
                  <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="5">5 minutes</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Offline Mode</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.data.offlineMode ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.data.offlineMode ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Data Usage</span>
                  <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="unlimited">Unlimited</option>
                    <option value="limited">Limited</option>
                    <option value="wifi-only">WiFi Only</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Security Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Biometric Auth</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.security.biometricAuth ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.security.biometricAuth ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">PIN Enabled</span>
                  <div className={`w-10 h-6 rounded-full ${mobileSettings.security.pinEnabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${mobileSettings.security.pinEnabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Session Timeout</span>
                  <select className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Offline Support */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Offline Support</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Offline Mode</span>
                  <div className={`w-10 h-6 rounded-full ${offlineSupport.enabled ? 'bg-blue-600' : 'bg-gray-300'} relative`}>
                    <div className={`w-4 h-4 bg-white rounded-full absolute top-1 ${offlineSupport.enabled ? 'right-1' : 'left-1'} transition-all`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Sync Status</span>
                  <div className="flex items-center space-x-2">
                    {offlineSupport.syncStatus === 'online' ? (
                      <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                        <Wifi className="h-4 w-4" />
                        <span className="text-sm">Online</span>
                      </div>
                    ) : offlineSupport.syncStatus === 'syncing' ? (
                      <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Syncing</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1 text-orange-600 dark:text-orange-400">
                        <WifiOff className="h-4 w-4" />
                        <span className="text-sm">Offline</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Last Sync</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {new Date(offlineSupport.lastSync).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Next Sync</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {new Date(offlineSupport.nextSync).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Smartphone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Mobile Access
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Work on the go
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <Menu className="h-5 w-5" />
              </button>
              <button className="relative p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <Bell className="h-5 w-5" />
                {mobileNotifications.badgeCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {mobileNotifications.badgeCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mobile Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.totalSessions}</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Smartphone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Session Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.averageSessionTime}m</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <Clock className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Offline Usage</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.offlineUsage}%</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <WifiOff className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Push Notifications</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{mobileStats.pushNotificationsSent}</p>
              </div>
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Bell className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <tab.icon className="h-4 w-4 inline-block mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentMobileAccess;
